import React, { useState } from "react";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { ExpandMore, ExpandLess, Close as CloseIcon } from "@mui/icons-material";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
import { useAuthContext } from "../../context/AuthContext";
import { getStatusChip } from "../../utils/utils";
import { getStatusLabelFromCode } from "../../utils/status";
import { useTranslation } from "react-i18next";

/**
 * Componente que exibe o histórico de mensagens de uma conversa.
 */
const MessageHistory = ({
  messages,
  selectedConversation,
  isLoadingConversation,
  onSendMessage,
  onEndConversation,
  onReopenRequest,
  onOpenNewRequest,
  hasMoreMessages,
  isLoadingMoreMessages,
  onLoadMoreMessages,
  onDeleteMessage,
}) => {
  const theme = useTheme();
  const { userData } = useAuthContext();
  const { t } = useTranslation("chat");
  const [isClosedBannerCollapsed, setIsClosedBannerCollapsed] = useState(false);

  const rawStatus = selectedConversation?.status;
  const numericStatus =
    typeof rawStatus === "string" ? Number(rawStatus) : rawStatus;
  const isClosedStatus =
    numericStatus === 2 || numericStatus === 4 || numericStatus === 5;

  if (isLoadingConversation) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          p: 2,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="35%" />
          <Skeleton variant="text" width="20%" sx={{ mt: 0.5 }} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, flex: 1 }}>
          <Skeleton variant="rounded" width="70%" height={48} />
          <Skeleton variant="rounded" width="55%" height={48} sx={{ alignSelf: "flex-end" }} />
          <Skeleton variant="rounded" width="65%" height={48} />
          <Skeleton variant="rounded" width="50%" height={48} sx={{ alignSelf: "flex-end" }} />
        </Box>
        <Box sx={{ pt: 2 }}>
          <Skeleton variant="rounded" width="100%" height={56} />
        </Box>
      </Box>
    );
  }

  if (!selectedConversation) {
    return (
      <Typography variant="h6" align="center" p={2}>
        {t("history.selectConversation")}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Lista de mensagens com virtualização */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflow: "hidden",
          padding: 2,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <MessageList
          key={selectedConversation.id}
          messages={messages}
          loggedUser={userData}
          onSendMessage={onSendMessage}
          defaultChannel={
            selectedConversation?.channel ||
            selectedConversation?.channelName ||
            selectedConversation?.channelType ||
            ""
          }
          hasMore={hasMoreMessages}
          isLoadingMore={isLoadingMoreMessages}
          onLoadMore={onLoadMoreMessages}
          onEndConversation={onEndConversation}
          onDeleteMessage={onDeleteMessage}
        />
      </Box>

      {/* Campo de entrada de mensagens / aviso de atendimento encerrado */}
      <Box
        sx={{
          flexShrink: 0,
          backgroundColor: theme.palette.background.paper,
          padding: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {isClosedStatus ? (
          isClosedBannerCollapsed ? (
            <Alert
              severity="info"
              icon={false}
              sx={{
                borderRadius: 2,
                py: 0.25,
                px: 0.75,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor:
                  theme.palette.mode === "light"
                    ? theme.palette.grey[50]
                    : theme.palette.background.default,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <IconButton
                size="small"
                aria-label={t("history.showClosedDetailsAria")}
                onClick={() => setIsClosedBannerCollapsed(false)}
              >
                <ExpandLess fontSize="small" />
              </IconButton>
            </Alert>
          ) : (
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                pt: 0.75,
                pb: 0.75,
                px: 1.75,
                backgroundColor:
                  theme.palette.mode === "light"
                    ? theme.palette.grey[50]
                    : theme.palette.background.default,
                border: `1px solid ${theme.palette.divider}`,
                mb: 0.75,
                position: "relative",
              }}
            >
              {/* X no canto superior direito */}
              <IconButton
                size="small"
                aria-label={t("history.closeClosedMessageAria")}
                onClick={() => setIsClosedBannerCollapsed(true)}
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                alignItems={{ xs: "flex-start", md: "flex-start" }}
              >
                {/* Coluna esquerda: título + textos */}
                <Stack
                  spacing={0.5}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    pr: { md: 2 },
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ flexWrap: "wrap" }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t("history.conversationClosed")}
                    </Typography>
                    {getStatusChip(numericStatus, theme)}
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.25 }}
                  >
                    {t("history.closedNoSend")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.25 }}
                  >
                    {t("history.closedContinueHint")}
                  </Typography>
                </Stack>

                {/* Coluna direita: botões empilhados */}
                <Stack
                  direction="column"
                  spacing={0.75}
                  justifyContent="flex-start"
                  alignItems="stretch"
                  sx={{
                    mt: { xs: 1, md: 1 },
                    minWidth: { md: 210 },
                  }}
                >
                  {typeof onOpenNewRequest === "function" && (
                    <Chip
                      label={t("history.newRequestChip")}
                      variant="outlined"
                      clickable
                      onClick={onOpenNewRequest}
                      sx={{ fontSize: "0.78rem", height: 24 }}
                    />
                  )}
                  {typeof onReopenRequest === "function" && (
                    <Chip
                      label={t("history.reopenChip")}
                      color="primary"
                      clickable
                      onClick={onReopenRequest}
                      sx={{ fontSize: "0.78rem", height: 24 }}
                    />
                  )}
                </Stack>
              </Stack>
            </Alert>
          )
        ) : (
          <MessageInput
            onSendMessage={onSendMessage}
            selectedConversation={selectedConversation}
            messages={messages}
            hasHistory={Array.isArray(messages) && messages.length > 0}
          />
        )}
      </Box>
    </Box>
  );
};

export default MessageHistory;
